import { Component, ElementRef, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import Swal from "sweetalert2";

import { GloControlComponent } from "../../../../shared/components/glo-control/glo-control.component";
import { ApiService } from "../../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";
import { UserDetails } from "../../../../shared/models/account-settings.model";
import { environment } from "../../../../../environments/environment";

@Component({
  selector: "business-account-details",
  imports: [GloControlComponent, ReactiveFormsModule, LoaderComponent],
  templateUrl: "./details.component.html",
  styleUrl: "./details.component.scss",
  standalone: true,
})
export class BusinessAccountDetailsComponent {
  assetUrl = environment.assetBaseUrl;

  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

  preview: string | ArrayBuffer | null = null;
  selectedImageBase64: string | null = null;

  accountDetailForm: FormGroup;
  isLoading = false;

  private lastLoadedData: any = null;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
  ) {
    this.accountDetailForm = this.fb.group({
      userName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      phone: ["", Validators.required],
      image: [""],
    });
  }

  ngOnInit(): void {
    this.getUserDetails();
  }

  triggerUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files[0]) return;

    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      this.preview = reader.result;
      this.selectedImageBase64 = reader.result as string;
      this.accountDetailForm.get("image")?.setValue(this.selectedImageBase64);
      this.accountDetailForm.get("image")?.markAsDirty();
    };

    reader.readAsDataURL(file);
  }

  populateUserDetailsForm(data: any): void {
    this.lastLoadedData = data;
    this.preview = data.image || null;

    this.accountDetailForm.patchValue({
      userName: data.userName || data.username || "",
      email: data.email || "",
      phone: data.phone || data.phone_number || "",
      image: data.image || "",
    });
  }

  isInvalid(path: string): boolean {
    const control = this.accountDetailForm.get(path);
    return !!(control?.invalid && (control.touched || control.dirty));
  }

  handleSave(): void {
    if (this.accountDetailForm.invalid) {
      this.accountDetailForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.apiService
      .request(
        API_ENDPOINTS.owner.accountSettings.userDetails.update,
        "POST",
        this.accountDetailForm.value,
      )
      .subscribe({
        next: () => {
          this.isLoading = false;

          // Swal.fire({
          //   title: "Success!",
          //   text: "User details updated successfully!",
          //   icon: "success",
          //   timer: 5000,
          //   showConfirmButton: false,
          //   toast: true,
          //   position: "top-end",
          //   customClass: {
          //     container: "!z-[999999999]",
          //   },
          // });

          this.getUserDetails();
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating user details:", error);

          Swal.fire({
            title: "Update Failed",
            text: "Something went wrong while saving your details.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
      });
  }

  getUserDetails(): void {
    this.isLoading = true;

    this.apiService
      .request<UserDetails>(
        API_ENDPOINTS.owner.accountSettings.userDetails.getUserDetails,
        "GET",
      )
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;

          if (response.data) {
            this.populateUserDetailsForm(response.data);
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error loading user details:", error);
        },
      });
  }

  onDelete(event: Event): void {
    event.preventDefault();

    this.accountDetailForm.get("image")?.setValue("");
    this.accountDetailForm.get("image")?.markAsDirty();
    this.preview = null;
    this.selectedImageBase64 = null;
  }

  resetForm(): void {
    if (this.lastLoadedData) {
      this.populateUserDetailsForm(this.lastLoadedData);
      this.accountDetailForm.markAsPristine();
      this.accountDetailForm.markAsUntouched();
      return;
    }

    this.accountDetailForm.reset();
    this.preview = null;
    this.selectedImageBase64 = null;
  }
}
